import { useContext, useEffect, useState } from "react";
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { SuggestionsTable } from "./suggestions-table";
import { REQUEST_STATUS } from "../../../shared/consts";
import { useChatContext } from "../../chat";
import { useFetchDimensions } from "../hooks/use-fetch-dimensions";
import { useWordSuggestionDialog } from "../hooks/use-word-suggestion-dialog";
import { UrlParamsContext } from "../../../app/context/url-params-context";

type WordSuggestionDialogProps = {
  open: boolean;
  setClose: () => void;
};

export const WordSuggestionDialog = ({
  open,
  setClose,
}: WordSuggestionDialogProps) => {
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<
    number | null
  >(null);
  const { functionKey } = useContext(UrlParamsContext);
  const { handleSuggestionClick, lastWord } = useChatContext();
  const { dimensions, dimensionRequestStatus } = useFetchDimensions(
    open,
    functionKey
  );

  const {
    inputValue,
    setInputValue,
    selectedDimension,
    setSelectedDimension,
    wordSuggestions,
    wordSuggestionsStatus,
    clearValues,
  } = useWordSuggestionDialog(functionKey, lastWord);

  useEffect(() => {
    if (open && !inputValue) {
      setInputValue(lastWord);
    }
  }, [lastWord, open]);

  const handleCancel = () => {
    clearValues();
    setSelectedSuggestionIndex(null);
    setClose();
  };

  const handleSelect = () => {
    if (selectedSuggestionIndex !== null) {
      const selectedSuggestion = wordSuggestions[selectedSuggestionIndex];
      handleSuggestionClick(selectedSuggestion);
    }
    handleCancel();
  };

  useEffect(() => {
    setSelectedSuggestionIndex(null);
  }, [inputValue, selectedDimension]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>Search All Words</DialogTitle>
      <DialogContent>
        <TextField
          label="Search"
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setInputValue(e.target.value)
          }
          fullWidth
          margin="normal"
          inputProps={{ maxLength: 100 }}
        />
        <FormControl sx={{ mt: 2, minWidth: 200 }}>
          <InputLabel id="dimension-select-label">Select Dimension</InputLabel>
          <Select
            labelId="dimension-select-label"
            value={selectedDimension}
            onChange={(e: SelectChangeEvent<string>) =>
              setSelectedDimension(e.target.value)
            }
            label="Select Dimension"
          >
            <MenuItem value="">All</MenuItem>
            {dimensionRequestStatus === REQUEST_STATUS.LOADING && (
              <MenuItem disabled>Loading...</MenuItem>
            )}
            {dimensionRequestStatus === REQUEST_STATUS.FAILURE && (
              <MenuItem disabled>
                Error loading dimensions, please try again later
              </MenuItem>
            )}
            {dimensionRequestStatus === REQUEST_STATUS.SUCCESS &&
              dimensions.map((dim, index) => (
                <MenuItem key={dim.tag} value={dim.tag}>
                  {dim.tag}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <SuggestionsTable
          wordSuggestionsStatus={wordSuggestionsStatus}
          wordSuggestions={wordSuggestions}
          inputValue={inputValue}
          selectedSuggestionIndex={selectedSuggestionIndex}
          setSelectedSuggestionIndex={setSelectedSuggestionIndex}
        />
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            onClick={handleCancel}
            color="secondary"
            sx={{
              ml: 2,
              mb: 1,
            }}
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSelect}
            sx={{
              mr: 2,
              mb: 1,
            }}
            disabled={selectedSuggestionIndex === null}
          >
            Select
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
